import React from 'react'
import {useLocation} from 'react-router-dom'
import {Box, VisuallyHidden} from '@chakra-ui/react'
// import {useLivePreviewCtx} from '../context/live-preview-context-provider'
// import {EntryProps} from '../typescript/components'
// import {Page} from '../typescript/pages'
import RenderComponents from '../../components/editorial-components/render-components'
import Skeleton from './skeleton'
import {useGetEditorialPage} from '../../custom-api/hooks/use-get-editorial-page'
import PlpStickyBar from '../../components/editorial-components/plp-sticky-bar'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {Helmet} from 'react-helmet'
import loadable from '@loadable/component'
import {MAX_CACHE_AGE_HP, STALE_WHILE_REVALIDATE_HP} from '../../constants'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getDamBaseUrl} from '../../custom-hooks/use-dam-image'
const PageNotFound = loadable(() => import('./../page-not-found'))

const getDesktopDamImageUrl = (imageSetObj) => {
    if (!imageSetObj) return null
    const desktop = imageSetObj?.desktop
    if (!desktop?.rendition || !desktop?.dam_image?.[0]?.id) return null
    return `${getDamBaseUrl()}/${desktop.rendition}/assets/${desktop.dam_image[0].id}`
}

const getHeroBannerImageUrl = (component) => {
    if (!component) return null
    for (const key of ['image_big', 'image_mid', 'image_small']) {
        const url = getDesktopDamImageUrl(component[key])
        if (url) return url
    }
    return null
}

const stripHtmlScripts = (html) => {
    if (!html) return ''
    return html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
}

const getFirstUlItems = (html) => {
    if (!html) return null
    const match = html.match(/<ul[^>]*>([\s\S]*?)<\/ul>/i)
    if (!match) return null
    const items = []
    const liRegex = /<li[^>]*>([\s\S]*?)<\/li>/gi
    let m
    while ((m = liRegex.exec(match[1])) !== null) {
        items.push(m[1].replace(/<[^>]+>/g, '').trim())
    }
    return items.length > 0 ? items : null
}

const EditorialPage = () => {
    const {res} = useServerContext()
    const {data, error, isFetching, isFetched, isLoading, isSuccess, isError} =
        useGetEditorialPage()
    const entries = {...data}
    const location = useLocation()
    const {customParams} = getConfig()
    const currentPath = 'https://' + customParams.currentHost + location.pathname

    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE_HP}, stale-while-revalidate=${STALE_WHILE_REVALIDATE_HP}`,
        )
    }

    if (error || (data === null && !isLoading)) {
        if (res) res.status(404)
        return <PageNotFound />
    }

    const seo = entries?.seo_standard || {}
    const metaTitle = seo?.meta_title
    const metaDescription = seo?.meta_description

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: metaTitle,
        description: metaDescription,
        url: currentPath,
    }

    const pathname = location.pathname
    const isArticle = pathname.includes('/e/magazine')
    const isRecipe = /\/e\/r(\/|$)/.test(pathname)
    const pageComponents = entries?.page_components || []

    let articleSchemaMarkup = null
    if (isArticle) {
        const heroBannerComponent = pageComponents.find(
            (c) => c._content_type_uid === 'herobanner' || c._content_type_uid === 'hero_banner',
        )
        const htmlComponent = pageComponents.find(
            (c) => c._content_type_uid === 'generic_html_content',
        )

        const heroImageUrl = getHeroBannerImageUrl(heroBannerComponent)
        const articleBody = stripHtmlScripts(htmlComponent?.body_copy)

        if (metaTitle && metaDescription && heroImageUrl && articleBody) {
            articleSchemaMarkup = {
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: metaTitle,
                description: metaDescription,
                image: heroImageUrl,
                articleBody,
            }
        }
    }

    let recipeSchemaMarkup = null
    if (isRecipe) {
        const sidecardComponent = pageComponents.find((c) => c._content_type_uid === 'sidecard')
        const textGroupComponents = pageComponents.filter(
            (c) => c._content_type_uid === 'text_group',
        )

        const recipeName = sidecardComponent?.text_group?.title
        const recipeDescription = sidecardComponent?.text_group?.bodycopystandard
        const sidecardImageSet = sidecardComponent?.image_standard
        const recipeImageUrl = getDesktopDamImageUrl(sidecardImageSet)

        const firstTextGroupHtml = textGroupComponents[0]?.body_copy
        const secondTextGroupHtml = textGroupComponents[1]?.body_copy
        const recipeIngredient = getFirstUlItems(firstTextGroupHtml)
        const recipeInstructions = getFirstUlItems(secondTextGroupHtml)

        if (
            recipeName &&
            recipeDescription &&
            recipeImageUrl &&
            recipeIngredient &&
            recipeInstructions
        ) {
            recipeSchemaMarkup = {
                '@context': 'https://schema.org',
                '@type': 'Recipe',
                name: recipeName,
                description: recipeDescription,
                image: recipeImageUrl,
                recipeIngredient,
                recipeInstructions: recipeInstructions.map((step) => ({
                    '@type': 'HowToStep',
                    text: step,
                })),
            }
        }
    }

    return (
        <Box
            data-gtm={location?.pathname === '/' ? 'homepage' : 'psp-page'}
            className="full-editorial"
        >
            <Helmet>
                {metaTitle && <title>{metaTitle}</title>}
                {metaDescription && <meta name="description" content={metaDescription} />}
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
                {articleSchemaMarkup && (
                    <script type="application/ld+json">
                        {JSON.stringify(articleSchemaMarkup, null, 2)}
                    </script>
                )}
                {recipeSchemaMarkup && (
                    <script type="application/ld+json">
                        {JSON.stringify(recipeSchemaMarkup, null, 2)}
                    </script>
                )}
            </Helmet>
            {seo?.h1_title && (
                <VisuallyHidden>
                    <h1>{seo.h1_title}</h1>
                </VisuallyHidden>
            )}
            {(isFetching || isLoading) &&
                !isFetched &&
                Array(entries?.page_components?.length || 4)
                    .fill()
                    .map((_, index) => <Skeleton key={index} />)}

            {data?.sticky_menu?.length > 0 && <PlpStickyBar data={data?.sticky_menu[0]} />}

            {isSuccess && !!Object.keys(entries).length && (
                <RenderComponents
                    pageComponents={entries?.page_components}
                    contentTypeUid="page"
                    entryUid={entries?.uid}
                    locale={entries?.locale}
                    isFetching={isFetching}
                />
            )}

            {isError && <>{/* handle error */}</>}
        </Box>
    )
}

export default EditorialPage
